import React, { Children, cloneElement, forwardRef, isValidElement, useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import './CardSwap.css';

export const Card = forwardRef(({ customClass, ...rest }, ref) => (
  <div ref={ref} {...rest} className={`card ${customClass ?? ''} ${rest.className ?? ''}`.trim()} />
));
Card.displayName = 'Card';

const makeSlot = (i, distX, distY, total) => ({
  x: i * distX,
  y: -i * distY,
  z: -i * distX * 1.5,
  zIndex: total - i
});
const placeNow = (el, slot, skew) =>
  el && gsap.set(el, {
    x: slot.x,
    y: slot.y,
    z: slot.z,
    xPercent: -50,
    yPercent: -50,
    skewY: skew,
    transformOrigin: 'center center',
    zIndex: slot.zIndex,
    force3D: true
  });

const EASING_PRESETS = {
  elastic: {
    ease: 'elastic.out(0.6,0.9)',
    durDrop: 2,
    durMove: 2,
    durReturn: 2,
    promoteOverlap: 0.9,
    returnDelay: 0.05,
    stagger: 0.15
  },
  // Soft cross-promote: no bounce, overlapping motion, gentle fade on exit
  smooth: {
    ease: 'power3.inOut',
    durDrop: 1.05,
    durMove: 1.05,
    durReturn: 0.95,
    promoteOverlap: 0.78,
    returnDelay: 0.12,
    stagger: 0.08
  },
  default: {
    ease: 'power1.inOut',
    durDrop: 0.8,
    durMove: 0.8,
    durReturn: 0.8,
    promoteOverlap: 0.45,
    returnDelay: 0.2,
    stagger: 0.15
  }
};

const CardSwap = ({
  width = 500,
  height = 400,
  cardDistance = 60,
  verticalDistance = 70,
  delay = 5000,
  pauseOnHover = false,
  onCardClick,
  skewAmount = 6,
  easing = 'elastic',
  dropDistance = 500,
  fadeOnSwap = false,
  /** 'stack' = original drop/promote; 'crossfade' = fade only, same position */
  mode = 'stack',
  children
}) => {
  const config = EASING_PRESETS[easing] || EASING_PRESETS.default;
  const isCrossfade = mode === 'crossfade';

  const childArr = useMemo(() => Children.toArray(children), [children]);
  const refs = useMemo(
    () => childArr.map(() => React.createRef()),
    [childArr.length]
  );

  const order = useRef(Array.from({ length: childArr.length }, (_, i) => i));

  const tlRef = useRef(null);
  const intervalRef = useRef();
  const container = useRef(null);

  useEffect(() => {
    const total = refs.length;
    const distX = isCrossfade ? 0 : cardDistance;
    const distY = isCrossfade ? 0 : verticalDistance;
    const skew = isCrossfade ? 0 : skewAmount;

    refs.forEach((r, i) => {
      placeNow(r.current, makeSlot(i, distX, distY, total), skew);
      if (r.current) {
        // Crossfade: only the front card is visible
        gsap.set(r.current, { opacity: isCrossfade ? (i === 0 ? 1 : 0) : 1 });
      }
    });

    const swap = () => {
      if (order.current.length < 2) return;

      const [front, ...rest] = order.current;
      const next = rest[0];
      const elFront = refs[front].current;
      const elNext = refs[next]?.current;
      const tl = gsap.timeline();
      tlRef.current = tl;

      if (isCrossfade) {
        // Simple opacity crossfade — no slide, no stack motion
        tl.to(elFront, {
          opacity: 0,
          duration: config.durDrop,
          ease: config.ease
        }, 0);
        if (elNext) {
          tl.set(elNext, { zIndex: total }, 0);
          tl.to(elNext, {
            opacity: 1,
            duration: config.durMove,
            ease: config.ease
          }, 0);
        }
        tl.call(() => {
          order.current = [...rest, front];
          order.current.forEach((idx, i) => {
            const el = refs[idx].current;
            if (!el) return;
            gsap.set(el, {
              zIndex: total - i,
              opacity: i === 0 ? 1 : 0,
              x: 0,
              y: 0,
              z: 0
            });
          });
        });
        return;
      }

      tl.to(elFront, {
        y: `+=${dropDistance}`,
        ...(fadeOnSwap ? { opacity: 0 } : {}),
        duration: config.durDrop,
        ease: config.ease
      });

      tl.addLabel('promote', `-=${config.durDrop * config.promoteOverlap}`);
      rest.forEach((idx, i) => {
        const el = refs[idx].current;
        const slot = makeSlot(i, distX, distY, refs.length);
        tl.set(el, { zIndex: slot.zIndex }, 'promote');
        tl.to(
          el,
          {
            x: slot.x,
            y: slot.y,
            z: slot.z,
            opacity: 1,
            duration: config.durMove,
            ease: config.ease
          },
          `promote+=${i * config.stagger}`
        );
      });

      const backSlot = makeSlot(refs.length - 1, distX, distY, refs.length);
      tl.addLabel('return', `promote+=${config.durMove * config.returnDelay}`);
      tl.call(
        () => {
          gsap.set(elFront, { zIndex: backSlot.zIndex });
        },
        undefined,
        'return'
      );
      tl.to(
        elFront,
        {
          x: backSlot.x,
          y: backSlot.y,
          z: backSlot.z,
          opacity: 1,
          duration: config.durReturn,
          ease: config.ease
        },
        'return'
      );

      tl.call(() => {
        order.current = [...rest, front];
      });
    };

    swap();
    intervalRef.current = window.setInterval(swap, delay);

    if (pauseOnHover) {
      const node = container.current;
      const pause = () => {
        tlRef.current?.pause();
        clearInterval(intervalRef.current);
      };
      const resume = () => {
        tlRef.current?.play();
        intervalRef.current = window.setInterval(swap, delay);
      };
      node.addEventListener('mouseenter', pause);
      node.addEventListener('mouseleave', resume);
      return () => {
        node.removeEventListener('mouseenter', pause);
        node.removeEventListener('mouseleave', resume);
        clearInterval(intervalRef.current);
      };
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDistance, verticalDistance, delay, pauseOnHover, skewAmount, easing, dropDistance, fadeOnSwap, mode]);

  const rendered = childArr.map((child, i) =>
    isValidElement(child)
      ? cloneElement(child, {
          key: i,
          ref: refs[i],
          style: { width, height, ...(child.props.style ?? {}) },
          onClick: e => {
            child.props.onClick?.(e);
            onCardClick?.(i);
          }
        })
      : child
  );

  return (
    <div ref={container} className="card-swap-container" style={{ width, height }}>
      {rendered}
    </div>
  );
};

export default CardSwap;
